import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { db, auth, storage } from '../firebaseConfig';
import { collection, query, where, onSnapshot, getDoc, doc, addDoc, getDocs, orderBy, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { IoArrowBack, IoReaderOutline, IoAddCircleOutline, IoSearchOutline, IoCloseOutline, IoPeopleOutline, IoCashOutline, IoDocumentTextOutline, IoCloudUploadOutline, IoWarning, IoShieldCheckmark } from 'react-icons/io5';
import moment from 'moment';
import './ConveniosScreen.css';
import kondifyLogo from '../assets/Kondify letra negra sin fondo.png';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import { useAuth } from '../context/AuthContext';

function ConveniosScreen() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState('');
    const [condoId, setCondoId] = useState('');
    const [firmaComite, setFirmaComite] = useState('');
    const [condoData, setCondoData] = useState(null);

    // List of agreements
    const [convenios, setConvenios] = useState([]);
    const [filteredConvenios, setFilteredConvenios] = useState([]);

    // Filter states
    const [filterEstatus, setFilterEstatus] = useState('todos'); // todos | activo | completado | cancelado
    const [filterSearch, setFilterSearch] = useState('');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);

    // Form state
    const [selectedResidenteId, setSelectedResidenteId] = useState('');
    const [saldoDeudorActual, setSaldoDeudorActual] = useState(0);
    const [interesPenalizacion, setInteresPenalizacion] = useState('');
    const [cuotaMensualOrdinaria, setCuotaMensualOrdinaria] = useState(0);
    const [montoTotalRefinanciado, setMontoTotalRefinanciado] = useState('');
    const [numeroMensualidades, setNumeroMensualidades] = useState('');
    const [montoMensual, setMontoMensual] = useState('');
    const [evidenciaFirmaUrl, setEvidenciaFirmaUrl] = useState('');

    // Editing State
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingConvenioId, setEditingConvenioId] = useState('');

    // Signature Canvas State
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [signingConvenioId, setSigningConvenioId] = useState('');
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // Rejection State
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionConvenioId, setRejectionConvenioId] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    // Available residents list for dropdown
    const [residentesList, setResidentesList] = useState([]);

    // Agreement File Upload State
    const [agreementFile, setAgreementFile] = useState(null);
    const [agreementPreviewName, setAgreementPreviewName] = useState('');

    // Custom Modal Dialog States
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        onConfirm: null
    });
    const [alertModal, setAlertModal] = useState({
        show: false,
        title: '',
        message: ''
    });

    const triggerConfirm = (title, message, onConfirm) => {
        setConfirmModal({ show: true, title, message, onConfirm });
    };

    const triggerAlert = (title, message) => {
        setAlertModal({ show: true, title, message });
    };

    // Auto-calculate Total Refinanced Amount
    useEffect(() => {
        const total = parseFloat(saldoDeudorActual || 0) + parseFloat(interesPenalizacion || 0);
        setMontoTotalRefinanciado(total > 0 ? total.toFixed(2) : '');
    }, [saldoDeudorActual, interesPenalizacion]);

    // Auto-calculate Monthly Payment
    useEffect(() => {
        const total = parseFloat(montoTotalRefinanciado || 0);
        const cuotasNum = parseInt(numeroMensualidades || 0);
        if (total > 0 && cuotasNum > 0) {
            setMontoMensual((total / cuotasNum).toFixed(2));
        } else {
            setMontoMensual('');
        }
    }, [montoTotalRefinanciado, numeroMensualidades]);

    useEffect(() => {
        if (!currentUser) return;
        loadUserInfo();
    }, [currentUser]);

    const loadUserInfo = async () => {
        try {
            const userRef = doc(db, 'usuarios', currentUser.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const uData = userSnap.data();
                setRole(uData.rol);
                setCondoId(uData.condominioId);
                subscribeConvenios(uData.condominioId, uData.rol);
                loadResidentes(uData.condominioId);

                // Fetch condo data (including committee signature)
                try {
                    const condoSnap = await getDoc(doc(db, 'condominios', uData.condominioId));
                    if (condoSnap.exists()) {
                        const cData = condoSnap.data();
                        setCondoData({ id: condoSnap.id, ...cData });
                        if (cData.conveniosConfig?.firmaComiteBase64) {
                            setFirmaComite(cData.conveniosConfig.firmaComiteBase64);
                        }
                    }
                } catch (condoErr) {
                    console.error("Error loading condo config:", condoErr);
                }

                // Fetch configured ordinary monthly maintenance fee
                try {
                    const configRef = doc(db, 'condominios', uData.condominioId, 'configuracion_cuotas', 'mensual');
                    const configSnap = await getDoc(configRef);
                    if (configSnap.exists()) {
                        setCuotaMensualOrdinaria(configSnap.data().monto || 0);
                    }
                } catch (configErr) {
                    console.error("Error loading monthly fee config:", configErr);
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    const loadResidentes = async (condoIdVal) => {
        try {
            const q = query(
                collection(db, 'usuarios'),
                where('condominioId', '==', condoIdVal),
                where('rol', '==', 'residente')
            );
            const snap = await getDocs(q);
            setResidentesList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
        }
    };

    const subscribeConvenios = (condoIdVal, userRole) => {
        let q;
        if (userRole === 'residente') {
            q = query(
                collection(db, 'convenios'),
                where('condominioId', '==', condoIdVal),
                where('residenteId', '==', currentUser.uid),
                orderBy('fechaCreacion', 'desc')
            );
        } else {
            q = query(
                collection(db, 'convenios'),
                where('condominioId', '==', condoIdVal),
                orderBy('fechaCreacion', 'desc')
            );
        }

        const unsub = onSnapshot(q, (snap) => {
            const list = snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                fechaCreacion: d.data().fechaCreacion?.toDate() || new Date()
            }));
            setConvenios(list);
            setFilteredConvenios(list);
            setLoading(false);
        }, (err) => {
            console.error(err);
            setLoading(false);
        });

        return () => unsub();
    };

    useEffect(() => {
        let list = [...convenios];

        if (filterEstatus !== 'todos') {
            list = list.filter(c => c.estatus === filterEstatus);
        }

        if (filterSearch.trim() !== '') {
            const s = filterSearch.toLowerCase();
            list = list.filter(c => 
                (c.residenteNombre && c.residenteNombre.toLowerCase().includes(s)) ||
                (c.unidad && c.unidad.toLowerCase().includes(s))
            );
        }

        setFilteredConvenios(list);
    }, [filterEstatus, filterSearch, convenios]);

    const handleCreateConvenio = async (e) => {
        e.preventDefault();
        if (!selectedResidenteId || !montoTotalRefinanciado || !numeroMensualidades || !montoMensual) {
            triggerAlert('Campos Incompletos', 'Por favor completa los campos requeridos.');
            return;
        }

        setModalLoading(true);
        try {
            const resData = residentesList.find(r => r.id === selectedResidenteId);
            if (!resData) throw new Error('Residente no encontrado');

            let finalEvidenciaUrl = evidenciaFirmaUrl;
            if (agreementFile) {
                const storageRef = ref(storage, `convenios/${condoId}/${Date.now()}_${agreementFile.name}`);
                const snapshot = await uploadBytes(storageRef, agreementFile);
                finalEvidenciaUrl = await getDownloadURL(snapshot.ref);
            }

            if (isEditMode && editingConvenioId) {
                // Update existing convenio
                const convenioRef = doc(db, 'convenios', editingConvenioId);
                await updateDoc(convenioRef, {
                    residenteId: selectedResidenteId,
                    residenteNombre: resData.nombre,
                    unidad: resData.numeroCasa || 'N/A',
                    montoTotalRefinanciado: parseFloat(montoTotalRefinanciado),
                    numeroMensualidades: parseInt(numeroMensualidades),
                    montoMensual: parseFloat(montoMensual),
                    estatus: 'pendiente_firma',
                    evidenciaFirmaUrl: finalEvidenciaUrl || '',
                    motivoRechazo: '' // Clear previous rejection reason
                });

                // Notify resident
                await addDoc(collection(db, 'notificaciones'), {
                    residenteId: selectedResidenteId,
                    condominioId: condoId,
                    titulo: 'Convenio Modificado',
                    mensaje: `El administrador ha corregido tu convenio de pago. Por favor revísalo y fírmalo para activarlo.`,
                    leido: false,
                    fechaCreacion: serverTimestamp()
                });

                triggerAlert('Convenio Actualizado', 'El convenio ha sido modificado y se ha enviado nuevamente al residente.');
            } else {
                // Create new convenio
                const newConvenio = {
                    condominioId: condoId,
                    residenteId: selectedResidenteId,
                    residenteNombre: resData.nombre,
                    unidad: resData.numeroCasa || 'N/A',
                    montoTotalRefinanciado: parseFloat(montoTotalRefinanciado),
                    numeroMensualidades: parseInt(numeroMensualidades),
                    montoMensual: parseFloat(montoMensual),
                    fechaInicio: serverTimestamp(),
                    estatus: 'pendiente_firma', // Start as pending
                    evidenciaFirmaUrl: finalEvidenciaUrl || '',
                    fechaCreacion: serverTimestamp()
                };

                await addDoc(collection(db, 'convenios'), newConvenio);

                // Send notification to resident
                await addDoc(collection(db, 'notificaciones'), {
                    residenteId: selectedResidenteId,
                    condominioId: condoId,
                    titulo: 'Convenio de Pago Pendiente',
                    mensaje: `Se ha generado un convenio de pago pendiente de tu firma por un monto de $${montoTotalRefinanciado}.`,
                    leido: false,
                    fechaCreacion: serverTimestamp()
                });

                triggerAlert('Convenio Registrado', 'Convenio registrado en espera de la firma del residente.');
            }

            setShowModal(false);
            // Reset form
            setIsEditMode(false);
            setEditingConvenioId('');
            setSelectedResidenteId('');
            setSaldoDeudorActual(0);
            setInteresPenalizacion('');
            setMontoTotalRefinanciado('');
            setNumeroMensualidades('');
            setMontoMensual('');
            setEvidenciaFirmaUrl('');
            setAgreementFile(null);
            setAgreementPreviewName('');
        } catch (e) {
            console.error(e);
            triggerAlert('Error', 'Error al registrar convenio: ' + e.message);
        } finally {
            setModalLoading(false);
        }
    };

    // Open Edit Mode
    const handleOpenEdit = (item) => {
        setIsEditMode(true);
        setEditingConvenioId(item.id);
        setSelectedResidenteId(item.residenteId);
        handleResidenteChange(item.residenteId);
        setMontoTotalRefinanciado(item.montoTotalRefinanciado);
        setNumeroMensualidades(item.numeroMensualidades);
        setMontoMensual(item.montoMensual);
        setEvidenciaFirmaUrl(item.evidenciaFirmaUrl || '');
        setAgreementFile(null);
        setAgreementPreviewName('');
        setShowModal(true);
    };

    // Canvas Drawing Handlers
    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#0f172a';
    };

    // Initialize signature canvas properties
    useEffect(() => {
        if (showSignatureModal && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.strokeStyle = '#0f172a';
        }
    }, [showSignatureModal]);

    const reestructurarYGenerarCargos = async (convenioId, cData) => {
        // 1. Mark existing pending/parcial cargos as reestructurado
        const qCargos = query(
            collection(db, 'cargos'),
            where('condominioId', '==', condoId),
            where('residenteId', '==', cData.residenteId),
            where('estatus', 'in', ['pendiente', 'parcial'])
        );
        const snapshotCargos = await getDocs(qCargos);
        
        let cargosDocs = snapshotCargos.docs;
        if (cargosDocs.length === 0 && cData.unidad) {
            const qCargosUnidad = query(
                collection(db, 'cargos'),
                where('condominioId', '==', condoId),
                where('unidadResidente', '==', cData.unidad),
                where('estatus', 'in', ['pendiente', 'parcial'])
            );
            const snapUnidad = await getDocs(qCargosUnidad);
            cargosDocs = snapUnidad.docs;
        }

        // Avoid duplicating if we already have generated convenio charges for this convenio
        const qAlreadyCreated = query(
            collection(db, 'cargos'),
            where('condominioId', '==', condoId),
            where('residenteId', '==', cData.residenteId),
            where('tipo', '==', 'Convenio')
        );
        const snapAlreadyCreated = await getDocs(qAlreadyCreated);
        const hasAlreadyGenerated = snapAlreadyCreated.docs.some(d => 
            d.data().concepto?.includes(`Convenio CONV-${convenioId.slice(0, 8).toUpperCase()}`)
        );

        if (hasAlreadyGenerated) {
            return false; // Already generated
        }

        // Update original cargos to status 'reestructurado'
        for (const cargoDoc of cargosDocs) {
            // Skip charges that are already part of a convenio
            if (cargoDoc.data().tipo === 'Convenio') continue;
            
            await updateDoc(doc(db, 'cargos', cargoDoc.id), {
                estatus: 'reestructurado',
                motivoReestructuracion: `Refinanciado en Convenio CONV-${convenioId.slice(0, 8).toUpperCase()}`
            });
        }

        // 2. Generate new monthly charges
        const totalMensualidades = parseInt(cData.numeroMensualidades);
        const montoMensualVal = parseFloat(cData.montoMensual);
        
        for (let i = 0; i < totalMensualidades; i++) {
            const vencimientoDate = new Date();
            vencimientoDate.setDate(vencimientoDate.getDate() + (30 * (i + 1))); // Next month, next 2 months, etc.

            await addDoc(collection(db, 'cargos'), {
                condominioId: condoId,
                residenteId: cData.residenteId,
                unidadResidente: cData.unidad,
                concepto: `Mensualidad ${i + 1}/${totalMensualidades} - Convenio CONV-${convenioId.slice(0, 8).toUpperCase()}`,
                monto: montoMensualVal,
                montoPagado: 0,
                estatus: 'pendiente',
                fechaVencimiento: vencimientoDate,
                fechaCreacion: new Date(),
                tipo: 'Convenio'
            });
        }
        return true;
    };

    const handleSincronizarCargosConvenio = async (item) => {
        setModalLoading(true);
        try {
            const generated = await reestructurarYGenerarCargos(item.id, item);
            if (generated) {
                triggerAlert('Sincronización Exitosa', 'Los cargos originales se han marcado como reestructurados y se han generado las mensualidades del convenio.');
            } else {
                triggerAlert('Sincronización', 'Los cargos ya estaban sincronizados para este convenio.');
            }
        } catch (e) {
            console.error(e);
            triggerAlert('Error', 'Error al sincronizar cargos: ' + e.message);
        } finally {
            setModalLoading(false);
        }
    };

    const handleSaveSignature = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const dataUrl = canvas.toDataURL('image/png');
        setModalLoading(true);
        try {
            const convenioRef = doc(db, 'convenios', signingConvenioId);
            await updateDoc(convenioRef, {
                firmaResidenteBase64: dataUrl,
                estatus: 'activo',
                fechaInicio: serverTimestamp() // Start active period upon signing
            });

            const snap = await getDoc(convenioRef);
            if (snap.exists()) {
                const cData = snap.data();
                
                // Restructure original cargos and generate new ones
                await reestructurarYGenerarCargos(signingConvenioId, cData);

                // Notify admin/committee members
                const qAdmins = query(
                    collection(db, 'usuarios'),
                    where('condominioId', '==', condoId),
                    where('rol', 'in', ['admin', 'comite'])
                );
                const adminsSnap = await getDocs(qAdmins);
                adminsSnap.docs.forEach(async (adminDoc) => {
                    await addDoc(collection(db, 'notificaciones'), {
                        residenteId: adminDoc.id,
                        condominioId: condoId,
                        titulo: 'Convenio Firmado',
                        mensaje: `El residente de la casa ${cData.unidad} ha firmado digitalmente el convenio de pago.`,
                        leido: false,
                        fechaCreacion: serverTimestamp()
                    });
                });
            }

            triggerAlert('Convenio Firmado', 'Has firmado el convenio digitalmente. El convenio ahora se encuentra ACTIVO.');
            setShowSignatureModal(false);
            setSigningConvenioId('');
        } catch (e) {
            console.error(e);
            triggerAlert('Error', 'Error al guardar firma: ' + e.message);
        } finally {
            setModalLoading(false);
        }
    };

    const handleRejectConvenio = async (e) => {
        e.preventDefault();
        if (!rejectionReason.trim()) {
            triggerAlert('Campo Requerido', 'Por favor especifica el motivo del rechazo.');
            return;
        }

        setModalLoading(true);
        try {
            const convenioRef = doc(db, 'convenios', rejectionConvenioId);
            await updateDoc(convenioRef, {
                estatus: 'rechazado',
                motivoRechazo: rejectionReason
            });

            const snap = await getDoc(convenioRef);
            if (snap.exists()) {
                const cData = snap.data();
                
                // Notify admin/committee members
                const qAdmins = query(
                    collection(db, 'usuarios'),
                    where('condominioId', '==', condoId),
                    where('rol', 'in', ['admin', 'comite'])
                );
                const adminsSnap = await getDocs(qAdmins);
                adminsSnap.docs.forEach(async (adminDoc) => {
                    await addDoc(collection(db, 'notificaciones'), {
                        residenteId: adminDoc.id,
                        condominioId: condoId,
                        titulo: 'Convenio Rechazado',
                        mensaje: `El residente de la casa ${cData.unidad} ha rechazado el convenio. Motivo: ${rejectionReason}`,
                        leido: false,
                        fechaCreacion: serverTimestamp()
                    });
                });
            }

            triggerAlert('Convenio Rechazado', 'Has rechazado el convenio. El administrador será notificado.');
            setShowRejectionModal(false);
            setRejectionConvenioId('');
            setRejectionReason('');
        } catch (e) {
            console.error(e);
            triggerAlert('Error', 'Error al rechazar convenio: ' + e.message);
        } finally {
            setModalLoading(false);
        }
    };

    const handleResidenteChange = async (resId) => {
        setSelectedResidenteId(resId);
        if (!resId) {
            setSaldoDeudorActual(0);
            setInteresPenalizacion('');
            setMontoTotalRefinanciado('');
            return;
        }
        try {
            const resData = residentesList.find(r => r.id === resId);
            if (!resData) return;

            // Fetch pending/parcial cargos for this resident
            const qCargos = query(
                collection(db, 'cargos'),
                where('condominioId', '==', condoId),
                where('residenteId', '==', resId),
                where('estatus', 'in', ['pendiente', 'parcial'])
            );
            const snap = await getDocs(qCargos);
            let totalDeuda = 0;
            snap.docs.forEach(docVal => {
                const data = docVal.data();
                totalDeuda += (data.monto || 0) - (data.montoPagado || 0);
            });

            // If resident ID had no direct cargos, try checking by their house number (unidad) if available
            if (totalDeuda === 0 && resData.numeroCasa) {
                const qCargosUnidad = query(
                    collection(db, 'cargos'),
                    where('condominioId', '==', condoId),
                    where('unidadResidente', '==', resData.numeroCasa),
                    where('estatus', 'in', ['pendiente', 'parcial'])
                );
                const snapUnidad = await getDocs(qCargosUnidad);
                snapUnidad.docs.forEach(docVal => {
                    const data = docVal.data();
                    totalDeuda += (data.monto || 0) - (data.montoPagado || 0);
                });
            }

            setSaldoDeudorActual(totalDeuda);
        } catch (e) {
            console.error('Error fetching debt for resident:', e);
        }
    };

    const handleUpdateEstatus = async (id, nuevoEstatus, residenteId) => {
        triggerConfirm(
            "Cambiar Estatus",
            `¿Seguro que deseas marcar este convenio como ${nuevoEstatus}?`,
            async () => {
                try {
                    await updateDoc(doc(db, 'convenios', id), {
                        estatus: nuevoEstatus
                    });

                    await addDoc(collection(db, 'notificaciones'), {
                        residenteId,
                        condominioId: condoId,
                        titulo: `Convenio de Pago ${nuevoEstatus.toUpperCase()}`,
                        mensaje: `Tu convenio de pago ahora se encuentra en estatus: ${nuevoEstatus}.`,
                        leido: false,
                        fechaCreacion: serverTimestamp()
                    });

                    triggerAlert('Convenio Actualizado', 'El estatus del convenio se ha actualizado correctamente.');
                } catch (e) {
                    console.error(e);
                    triggerAlert('Error', 'Error al actualizar convenio: ' + e.message);
                }
            }
        );
    };

    const downloadConvenioPDF = async (item) => {
        const doc = new jsPDF();
        
        // Brand color
        const kondoPurple = [115, 87, 250];

        // --- Header (Condo-focused) ---
        doc.setFontSize(18);
        doc.setTextColor(15, 23, 42); // Slate-900
        doc.setFont("helvetica", "bold");
        
        // Condo Name or fallback
        const condoTitle = condoData?.nombre ? condoData.nombre.toUpperCase() : "ADMINISTRACIÓN DEL CONDOMINIO";
        doc.text(condoTitle, 14, 18);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        doc.text("Acuerdo de Reestructuración y Convenio de Pago Condominial", 14, 24);
        doc.text(`Identificador de Condominio: ${condoId || 'N/A'}`, 14, 30);

        // --- Line separation ---
        doc.setDrawColor(220, 225, 230);
        doc.line(14, 36, 196, 36);

        // --- Title ---
        doc.setFontSize(13);
        doc.setTextColor(30, 41, 59);
        doc.setFont("helvetica", "bold");
        doc.text("CONVENIO DE PAGO", 14, 48);

        // --- Metadata Box ---
        doc.setFillColor(248, 250, 252);
        doc.rect(14, 54, 182, 30, "F");
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        
        doc.text(`Folio del Convenio: CONV-${item.id.slice(0, 8).toUpperCase()}`, 18, 60);
        doc.text(`Fecha de Emisión: ${moment(item.fechaCreacion).format('DD/MM/YYYY')}`, 18, 66);
        doc.text(`Estatus del Convenio: ${item.estatus.toUpperCase()}`, 18, 72);
        doc.text(`Vigencia: Hasta cumplimiento total de mensualidades`, 18, 78);

        // --- Resident / Unit Info ---
        doc.setFontSize(12);
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold");
        doc.text("DATOS DEL RESIDENTE Y UNIDAD", 14, 94);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 65, 85);
        doc.text(`Nombre del Propietario / Residente: ${item.residenteNombre}`, 14, 102);
        doc.text(`Unidad Habitacional (Casa): Casa ${item.unidad}`, 14, 108);

        // --- Financial breakdown table ---
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text("DETALLES DEL PLAN DE REFINANCIAMIENTO", 14, 122);

        const origDebt = item.montoTotalRefinanciado - (parseFloat(item.interesPenalizacion) || 0);

        const tableColumn = ["Concepto", "Detalle / Especificación", "Monto (MXN)"];
        const tableRows = [
            ["Deuda de Origen", "Adeudos y cuotas acumuladas anteriores", `$${origDebt.toFixed(2)}`],
            ["Cargos Adicionales", "Intereses moratorios y penalizaciones acordadas", `$${(parseFloat(item.interesPenalizacion) || 0).toFixed(2)}`],
            ["Monto Total Refinanciado", "Suma total refinanciada sujeta a este convenio", `$${item.montoTotalRefinanciado.toFixed(2)}`],
            ["Plazo del Convenio", `${item.numeroMensualidades} mensualidades consecutivas`, "-"],
            ["Monto de Mensualidad", "Pago por amortización mensual fija", `$${item.montoMensual.toFixed(2)}`],
        ];

        autoTable(doc, {
            startY: 128,
            head: [tableColumn],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: kondoPurple },
            styles: { fontSize: 9 }
        });

        const nextY = doc.lastAutoTable.finalY + 15;

        // --- Legal disclaimer ---
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(100);
        const disclaimer = "El residente se compromete a realizar los pagos mensuales descritos en este convenio. En caso de incumplimiento de una o más mensualidades consecutivas, la administración o el comité de vigilancia se reserva el derecho de rescindir el presente convenio y reactivar las penalizaciones ordinarias y/o restringir los accesos automatizados según el reglamento vigente.";
        const splitDisclaimer = doc.splitTextToSize(disclaimer, 180);
        doc.text(splitDisclaimer, 14, nextY);

        // --- Signatures section ---
        const sigY = nextY + 30;

        // Left Side: Resident line
        doc.setDrawColor(150);
        doc.line(20, sigY, 90, sigY);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 65, 85);
        doc.text("Firma del Residente", 35, sigY + 5);
        doc.setFontSize(8);
        doc.text(item.residenteNombre, 35, sigY + 9);

        // Stamp resident base64 signature if available!
        if (item.firmaResidenteBase64) {
            try {
                doc.addImage(item.firmaResidenteBase64, 'PNG', 25, sigY - 22, 50, 20);
            } catch (err) {
                console.error("Error drawing resident signature image in PDF:", err);
            }
        }

        // Right Side: Committee / Admin signature
        doc.line(120, sigY, 190, sigY);
        doc.setFontSize(9);
        doc.text("Firma del Comité / Administración", 125, sigY + 5);

        // Stamp committee base64 signature if available!
        if (firmaComite) {
            try {
                // Since base64 signature could be PNG/JPEG, add it above the line
                doc.addImage(firmaComite, 'PNG', 125, sigY - 22, 50, 20);
            } catch (err) {
                console.error("Error drawing signature image in PDF:", err);
            }
        } else {
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text("[ Firma Digital no Registrada ]", 128, sigY - 6);
        }

        // --- Footer ---
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(150);
        doc.text("Documento digital generado a través de Kondify. Válido sin sellos físicos.", 14, 285);

        // --- Subtle branding logo at bottom right ---
        try {
            const logoImg = await new Promise((resolve, reject) => {
                const img = new Image();
                img.src = kondifyLogo;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
            const logoWidth = 24; // width in mm
            const imgWidth = logoImg.naturalWidth || logoImg.width || 1;
            const imgHeight = logoImg.naturalHeight || logoImg.height || 1;
            const logoHeight = (imgHeight / imgWidth) * logoWidth;
            
            // Render at bottom right, aligning the bottom edge around y = 285
            doc.addImage(logoImg, 'PNG', 170, 285 - logoHeight, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Branding logo load failed:", e);
        }

        // Save PDF
        doc.save(`Convenio_Pago_${item.unidad}_CONV-${item.id.slice(0,8).toUpperCase()}.pdf`);
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/convenios" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader title="Administración" showBackButton={false} />
                </div>

                <div className="ks-scroll-area">
                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Convenios de Pago</h1>
                                <p>Historial y refinanciamientos</p>
                                {['admin', 'comite'].includes(role) && (
                                    <button className="hero-publish-btn" onClick={() => setShowModal(true)}>
                                        <IoAddCircleOutline size={18} />
                                        <span>Convenio</span>
                                    </button>
                                )}
                            </div>
                            <IoReaderOutline className="muro-hero-icon" />
                        </div>
                    </div>

                    <div className="convenios-dashboard-body">

                        {/* Filters and Actions Bar */}
                        <div className="actions-bar-container">
                            <div className="search-box">
                                <IoSearchOutline size={18} />
                                <input
                                    type="text"
                                    placeholder="Buscar por casa o residente..."
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                />
                            </div>

                            <div className="filters-right">
                                <select 
                                    className="filter-select"
                                    value={filterEstatus}
                                    onChange={(e) => setFilterEstatus(e.target.value)}
                                >
                                    <option value="todos">Todos los estatus</option>
                                    <option value="activo">Activos</option>
                                    <option value="completado">Completados</option>
                                    <option value="cancelado">Cancelados</option>
                                </select>
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-state">Cargando convenios de pago...</div>
                        ) : filteredConvenios.length === 0 ? (
                            <div className="empty-state">
                                <IoReaderOutline size={60} style={{ color: '#cbd5e1', marginBottom: 15 }} />
                                <h3>Sin convenios registrados</h3>
                                <p>No se encontraron convenios vigentes o finalizados con los filtros actuales.</p>
                            </div>
                        ) : (
                            <div className="records-list">
                                {filteredConvenios.map((item) => (
                                    <div key={item.id} className={`record-card convenio-card status-${item.estatus}`}>
                                        <div className="record-card-header">
                                            <span className={`badge-tipo estatus-${item.estatus}`}>
                                                {item.estatus === 'pendiente_firma' ? 'Pendiente Firma' : 
                                                 item.estatus === 'rechazado' ? 'Rechazado' : 
                                                 item.estatus.toUpperCase()}
                                            </span>
                                            <span className="record-date">Creado el {moment(item.fechaCreacion).format('D MMM YYYY')}</span>
                                        </div>
                                        <div className="record-card-content">
                                            <div className="record-field">
                                                <IoPeopleOutline className="field-icon" />
                                                <span>Residente: <strong>{item.residenteNombre}</strong> (Casa {item.unidad})</span>
                                            </div>
                                            <div className="record-field">
                                                <IoCashOutline className="field-icon" />
                                                <span>Refinanciado: <strong>${item.montoTotalRefinanciado} MXN</strong> a <strong>{item.numeroMensualidades} meses</strong></span>
                                            </div>
                                            <div className="record-field">
                                                <IoCashOutline className="field-icon" />
                                                <span>Cuota Mensual: <strong>${item.montoMensual} MXN</strong></span>
                                            </div>
                                            {item.estatus === 'rechazado' && item.motivoRechazo && (
                                                <div className="rejection-reason-box" style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#fff5f5', borderLeft: '3px solid #f87171', color: '#991b1b', fontSize: '0.85rem', borderRadius: '4px' }}>
                                                    <strong>Motivo de rechazo:</strong> {item.motivoRechazo}
                                                </div>
                                            )}
                                            <div className="record-evidence" style={{ marginTop: '8px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                                {item.evidenciaFirmaUrl && (
                                                    <a href={item.evidenciaFirmaUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        <IoDocumentTextOutline size={16} /> Ver Anexo Firmado
                                                    </a>
                                                )}
                                                {item.firmaResidenteBase64 && (
                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#059669' }}>
                                                        <IoShieldCheckmark size={16} /> Firmado Digitalmente
                                                    </div>
                                                )}
                                                <button 
                                                    type="button"
                                                    onClick={() => downloadConvenioPDF(item)} 
                                                    style={{ background: 'none', border: 'none', padding: 0, color: '#4f46e5', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', font: 'inherit', fontWeight: '500' }}
                                                >
                                                    <IoDocumentTextOutline size={16} /> Descargar Convenio PDF
                                                </button>
                                            </div>

                                            {/* Resident signing actions */}
                                            {role === 'residente' && item.estatus === 'pendiente_firma' && (
                                                <div className="convenio-actions-bar" style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                                    <button 
                                                        className="btn-convenio-action complete" 
                                                        style={{ background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                                                        onClick={() => {
                                                            setSigningConvenioId(item.id);
                                                            setShowSignatureModal(true);
                                                        }}
                                                    >
                                                        Firmar Convenio
                                                    </button>
                                                    <button 
                                                        className="btn-convenio-action cancel" 
                                                        style={{ background: '#ef4444', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                                                        onClick={() => {
                                                            setRejectionConvenioId(item.id);
                                                            setShowRejectionModal(true);
                                                        }}
                                                    >
                                                        Rechazar
                                                    </button>
                                                </div>
                                            )}

                                            {/* Admin management actions */}
                                            {['admin', 'comite'].includes(role) && (
                                                <div className="convenio-actions-bar" style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
                                                    {item.estatus === 'activo' && (
                                                        <>
                                                            <button className="btn-convenio-action complete" onClick={() => handleUpdateEstatus(item.id, 'completado', item.residenteId)}>
                                                                Completado
                                                            </button>
                                                            <button className="btn-convenio-action cancel" onClick={() => handleUpdateEstatus(item.id, 'cancelado', item.residenteId)}>
                                                                Cancelar
                                                            </button>
                                                            <button 
                                                                className="btn-convenio-action edit" 
                                                                style={{ background: '#3b82f6', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                                                                onClick={() => handleSincronizarCargosConvenio(item)}
                                                            >
                                                                Sincronizar Cargos
                                                            </button>
                                                        </>
                                                    )}
                                                    {(item.estatus === 'pendiente_firma' || item.estatus === 'rechazado') && (
                                                        <button 
                                                            className="btn-convenio-action edit" 
                                                            style={{ background: '#f59e0b', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                                                            onClick={() => handleOpenEdit(item)}
                                                        >
                                                            Modificar Convenio
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Create/Edit Convenio Modal */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => {
                        setShowModal(false);
                        setIsEditMode(false);
                        setEditingConvenioId('');
                    }}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>{isEditMode ? 'Modificar Convenio de Pago' : 'Crear Convenio de Pago'}</h3>
                                <button className="close-btn" onClick={() => {
                                    setShowModal(false);
                                    setIsEditMode(false);
                                    setEditingConvenioId('');
                                }}>
                                    <IoCloseOutline size={24} />
                                </button>
                            </div>
                            <form onSubmit={handleCreateConvenio} className="modal-form">
                                <div className="form-group">
                                    <label>Residente Deudor *</label>
                                    <select
                                        value={selectedResidenteId}
                                        onChange={(e) => handleResidenteChange(e.target.value)}
                                        required
                                        disabled={isEditMode}
                                    >
                                        <option value="" disabled>Selecciona un residente...</option>
                                        {/* In edit mode, we want to show all residents in the list so that we can render the selected one */}
                                        {isEditMode ? (
                                            residentesList.map(r => (
                                                <option key={r.id} value={r.id}>{r.nombre} (Casa {r.numeroCasa || 'N/A'})</option>
                                            ))
                                        ) : (
                                            residentesList.filter(r => r.isMoroso).map(r => (
                                                <option key={r.id} value={r.id}>{r.nombre} (Casa {r.numeroCasa || 'N/A'})</option>
                                            ))
                                        )}
                                    </select>
                                </div>

                                {selectedResidenteId && !isEditMode && (
                                    <div className="info-box-debt" style={{ padding: '10px', backgroundColor: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', color: '#991b1b', fontSize: '0.9rem', marginBottom: '15px' }}>
                                        <strong>Deuda pendiente actual:</strong> ${saldoDeudorActual.toFixed(2)} MXN
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Monto Total a Refinanciar ($ MXN) *</label>
                                    <input
                                        type="number"
                                        placeholder="Ej: 5400"
                                        value={montoTotalRefinanciado}
                                        onChange={(e) => setMontoTotalRefinanciado(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Número de Mensualidades *</label>
                                    <input
                                        type="number"
                                        placeholder="Ej: 6"
                                        value={numeroMensualidades}
                                        onChange={(e) => setNumeroMensualidades(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Monto Mensual Acordado ($ MXN) *</label>
                                    <input
                                        type="number"
                                        placeholder="Ej: 900"
                                        value={montoMensual}
                                        onChange={(e) => setMontoMensual(e.target.value)}
                                        required
                                    />
                                </div>

                                {selectedResidenteId && montoMensual && (
                                    <div className="info-box-breakdown" style={{ padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #dcfce7', borderRadius: '8px', color: '#166534', fontSize: '0.85rem', marginTop: '10px', marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Mensualidad del convenio:</span>
                                            <strong>${parseFloat(montoMensual).toFixed(2)} MXN</strong>
                                        </div>
                                        {cuotaMensualOrdinaria > 0 && (
                                            <>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Cuota mensual ordinaria actual:</span>
                                                    <strong>${parseFloat(cuotaMensualOrdinaria).toFixed(2)} MXN</strong>
                                                </div>
                                                <hr style={{ border: 'none', borderTop: '1px solid #bbf7d0', margin: '6px 0' }} />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: 'bold' }}>
                                                    <span>Pago total mensual estimado:</span>
                                                    <span>${(parseFloat(montoMensual) + parseFloat(cuotaMensualOrdinaria)).toFixed(2)} MXN</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                 <div className="form-group">
                                     <label>Documento de Respaldo / Minuta (Opcional)</label>
                                     <div className="file-input-wrapper">
                                         <div className="file-input-button" onClick={() => document.getElementById('agreement-file-input').click()} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '12px', border: '1px dashed #cbd5e1', borderRadius: '10px', background: '#f8fafc', color: '#475569', justifyContent: 'center' }}>
                                             <IoCloudUploadOutline size={22} />
                                             <span style={{ fontSize: '0.9rem' }}>{agreementFile ? agreementFile.name : (evidenciaFirmaUrl ? "Documento cargado (toca para cambiar)" : "Toca para adjuntar documento PDF o imagen")}</span>
                                         </div>
                                         <input
                                             type="file"
                                             id="agreement-file-input"
                                             accept=".pdf,image/*"
                                             style={{ display: 'none' }}
                                             onChange={(e) => {
                                                 if (e.target.files[0]) {
                                                     setAgreementFile(e.target.files[0]);
                                                 }
                                             }}
                                         />
                                     </div>
                                 </div>

                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={() => {
                                        setShowModal(false);
                                        setIsEditMode(false);
                                        setEditingConvenioId('');
                                    }} disabled={modalLoading}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-submit" disabled={modalLoading}>
                                        {modalLoading ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Iniciar Convenio')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Digital Signature Canvas Modal */}
                {showSignatureModal && (
                    <div className="modal-overlay" onClick={() => {
                        if (!modalLoading) setShowSignatureModal(false);
                    }}>
                        <div className="modal-content premium-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                            <div className="modal-header">
                                <h3>Firmar Convenio Digitalmente</h3>
                                <button className="close-btn" onClick={() => setShowSignatureModal(false)} disabled={modalLoading}>
                                    <IoCloseOutline size={24} />
                                </button>
                            </div>
                            <div style={{ padding: '15px' }}>
                                <p style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '15px' }}>
                                    Dibuja tu firma en el recuadro inferior. Al presionar "Aceptar y Firmar" aceptas las condiciones y mensualidades del convenio.
                                </p>
                                <div style={{ border: '2px dashed #cbd5e1', borderRadius: '12px', overflow: 'hidden', background: '#f8fafc', position: 'relative' }}>
                                    <canvas
                                        ref={canvasRef}
                                        width={460}
                                        height={200}
                                        onMouseDown={startDrawing}
                                        onMouseMove={draw}
                                        onMouseUp={stopDrawing}
                                        onMouseLeave={stopDrawing}
                                        onTouchStart={startDrawing}
                                        onTouchMove={draw}
                                        onTouchEnd={stopDrawing}
                                        style={{ display: 'block', cursor: 'crosshair', width: '100%', height: '200px', touchAction: 'none' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
                                    <button 
                                        type="button" 
                                        onClick={clearCanvas} 
                                        style={{ background: 'none', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', color: '#475569' }}
                                    >
                                        Limpiar Lienzo
                                    </button>
                                </div>
                            </div>
                            <div className="form-actions" style={{ padding: '15px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button 
                                    type="button" 
                                    className="btn-cancel" 
                                    onClick={() => setShowSignatureModal(false)} 
                                    disabled={modalLoading}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="button" 
                                    className="btn-submit" 
                                    onClick={handleSaveSignature} 
                                    disabled={modalLoading}
                                >
                                    {modalLoading ? 'Firmando...' : 'Aceptar y Firmar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Rejection Modal */}
                {showRejectionModal && (
                    <div className="modal-overlay" onClick={() => {
                        if (!modalLoading) setShowRejectionModal(false);
                    }}>
                        <div className="modal-content premium-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                            <div className="modal-header">
                                <h3>Rechazar Convenio</h3>
                                <button className="close-btn" onClick={() => setShowRejectionModal(false)} disabled={modalLoading}>
                                    <IoCloseOutline size={24} />
                                </button>
                            </div>
                            <form onSubmit={handleRejectConvenio}>
                                <div style={{ padding: '15px' }}>
                                    <div className="form-group">
                                        <label>Especifica el motivo de rechazo *</label>
                                        <textarea
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            placeholder="Ej: El monto total no coincide con lo acordado..."
                                            required
                                            style={{ width: '100%', height: '100px', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                                        />
                                    </div>
                                </div>
                                <div className="form-actions" style={{ padding: '15px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button 
                                        type="button" 
                                        className="btn-cancel" 
                                        onClick={() => setShowRejectionModal(false)} 
                                        disabled={modalLoading}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn-submit" 
                                        style={{ background: '#ef4444' }} 
                                        disabled={modalLoading}
                                    >
                                        {modalLoading ? 'Rechazando...' : 'Confirmar Rechazo'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Custom Confirmation Modal */}
                {confirmModal.show && (
                    <div className="confirm-modal-overlay">
                        <div className="modal-content confirm-modal-content premium-popup">
                            <div className="confirm-icon-wrapper warning-icon-wrapper" style={{ backgroundColor: '#fee2e2', color: '#ef4444' }}>
                                <IoWarning size={46} className="confirm-icon" />
                            </div>
                            <h3>{confirmModal.title}</h3>
                            <p className="confirm-message">{confirmModal.message}</p>
                            <div className="confirm-actions">
                                <button 
                                    type="button" 
                                    className="btn-cancelar" 
                                    onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="button" 
                                    className="btn-confirmar-entrega" 
                                    onClick={() => {
                                        if (confirmModal.onConfirm) confirmModal.onConfirm();
                                        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
                                    }}
                                    style={{ background: 'linear-gradient(135deg, #7B61FF, #6348D1)', boxShadow: '0 4px 12px rgba(123, 97, 255, 0.25)' }}
                                >
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Custom Alert Modal */}
                {alertModal.show && (
                    <div className="confirm-modal-overlay">
                        <div className="modal-content confirm-modal-content premium-popup">
                            <div className="confirm-icon-wrapper success-icon-wrapper" style={{ backgroundColor: '#ecfdf5', color: '#10b981' }}>
                                <IoShieldCheckmark size={46} className="confirm-icon" />
                            </div>
                            <h3>{alertModal.title}</h3>
                            <p className="confirm-message">{alertModal.message}</p>
                            <div className="confirm-actions">
                                <button 
                                    type="button" 
                                    className="btn-confirmar-entrega" 
                                    onClick={() => setAlertModal({ show: false, title: '', message: '' })}
                                    style={{ width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', flex: 'none', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
                                >
                                    Aceptar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <KondoBottomNav activeRoute="/convenios" />
            </main>
        </div>
    );
}

export default ConveniosScreen;
